import {
  Box,
  Button,
  FormControl,
  FormLabel,
  makeStyles,
  TextField,
  Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useRecoilValue } from "recoil";
import Delayed from "~/src/components/Delayed";
import LoaderOverlay from "~/src/components/LoaderOverlay";
import useClearEffect from "../hooks/useClearEffect";
import useConfirmSignup from "../hooks/useConfirmSignup";
import {
  encodedEmailSelector,
  errorKeySelector,
  isLoadingState,
} from "../store";
import Layout from "./NewLayout";

const useStyles = makeStyles(({ spacing }) => ({
  container: {
    overflow: "hidden",
    "& .confirmAccountTitle": {
      fontWeight: 600,
      fontSize: "22px",
      lineHeight: "1.6",
      margin: "0 0 12px 0",
    },
    "& .confirmAccountButton": {
      background: "#000000",
      color: "#FFFFFF",
      borderColor: "#000000",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: ".5rem",
      lineHeight: "1.6",
      padding: "12px 32px",
      borderRadius: "25px",
      fontWeight: 600,
      fontSize: "16px",
      marginBottom: "12px",
      border: "2px solid transparent",
      "&.Mui-disabled": {
        color: "#a6a6a6",
        borderColor: "rgba(0, 0, 0, 0.12)",
        backgroundColor: "rgba(0, 0, 0, 0.12)",
      },
    },
  },
  formControlContainer: {
    "& label": {
      padding: 0,
      fontWeight: 600,
      lineHeight: "1.6",
      fontSize: "13px",
      marginBottom: "6px",
      color: "var(--black)",
    },
    "& .MuiFilledInput-root": {
      background: "none",
      borderRadius: "0",
    },
    "& input": {
      width: "100%",
      border: "1.5px solid #aba69c",
      borderRadius: "10px !important",
      fontSize: "15px",
    },
  },
  resendButton: {
    fontWeight: 600,
    fontSize: "1rem",
    lineHeight: 1.5,
  },
}));

const ConfirmAccount: React.FC = () => {
  const { container, formControlContainer, resendButton } = useStyles();

  const { t } = useTranslation("auth");
  const { push } = useHistory();
  const [confirmSignup, resendConfirmationCode] = useConfirmSignup();
  const user = useRecoilValue(encodedEmailSelector);
  const signUpPending = useRecoilValue(isLoadingState);
  const errorKey = useRecoilValue(errorKeySelector);
  const [confirmationCodeTouched, setConfirmationCodeTouched] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState("");

  useEffect(() => {
    if (!user) {
      push("/login");
    }
  }, []);

  useClearEffect();

  const canConfirmSignUp = useMemo(
    () => user && !!confirmationCode,
    [user, confirmationCode]
  );

  const handleConfirmCodeChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmationCodeTouched(true);
    setConfirmationCode(e.target.value);
  };

  const handleConfirmSignup = () => {
    confirmSignup(confirmationCode);
  };

  const handleEnterKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter" && canConfirmSignUp) {
      handleConfirmSignup();
    }
  };

  return (
    <Layout
      chipText={t("confirmSignUp.layoutContainer.title")}
      description={t("confirmSignUp.layoutContainer.description")}
      cursorAfterText={t("confirmSignUp.layoutContainer.cursorAfterText")}
      cursorBeforeText={t("confirmSignUp.layoutContainer.cursorBeforeText")}
    >
      <Box className={container}>
        {signUpPending && (
          <Delayed>
            <LoaderOverlay />
          </Delayed>
        )}
        <Box flex="auto">
          <Typography variant="h3" className="confirmAccountTitle">
            {t("confirmSignUp.title")}
          </Typography>
          {!!errorKey && (
            <Box pb={3}>
              <Alert severity="error">{t(errorKey)}</Alert>
            </Box>
          )}
          <Box mb={3}>
            <FormControl className={formControlContainer} fullWidth required>
              <FormLabel>{t("confirmSignUp.confirmCode.label")}</FormLabel>
              <TextField
                autoComplete="off"
                type="text"
                variant="filled"
                placeholder={t("confirmSignUp.confirmCode.placeholder")}
                hiddenLabel
                InputProps={{ disableUnderline: true }}
                FormHelperTextProps={{ variant: "standard" }}
                value={confirmationCode}
                onChange={handleConfirmCodeChanged}
                onKeyPress={handleEnterKeyPress}
                error={confirmationCodeTouched && !confirmationCode}
                helperText={
                  confirmationCodeTouched &&
                  !confirmationCode &&
                  t("confirmSignUp.confirmCode.required")
                }
              />
            </FormControl>
          </Box>
          <Button
            fullWidth
            className="confirmAccountButton"
            variant="contained"
            color="primary"
            size="large"
            disabled={!canConfirmSignUp}
            onClick={handleConfirmSignup}
          >
            {t("confirmSignUp.buttons.signUp")}
          </Button>
          <Box display="flex" alignItems="center">
            <Typography component="span" style={{ fontSize: "15px" }}>
              {t("confirmSignUp.resendCode.message")}
            </Typography>
            <Button
              color="secondary"
              onClick={resendConfirmationCode}
              className={resendButton}
              style={{ fontSize: "15px" }}
            >
              {t("confirmSignUp.resendCode.link")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default React.memo(ConfirmAccount);
